import React, {useEffect, useRef, useCallback, useState} from "react";
import {createSearchParams, generatePath, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Card, Col, Row, Spin, Image, Grid, Button, FloatButton, Slider} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {fetchCardsListAction, fetchOneCardAction, ROUTE_PATH, selectStore} from "../../../../utils";
import {MyText} from "../../../../components";
import {LeftOutlined, ArrowUpOutlined} from "@ant-design/icons";
import {ButtonContainer, ImageWrapper, StyledCard, StyledImage} from "./style";
import {useTranslation} from "react-i18next";
import {SoundFilled, PauseOutlined, CaretRightFilled} from '@ant-design/icons';
import { useEmbeddedNavigate } from "../../../../utils/context/embed";
import { Helmet } from "react-helmet";
export const CurrentCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const embeddedNavigate = useEmbeddedNavigate();

  const [searchParams] = useSearchParams();
  const {t} = useTranslation("common");
  const {currentCard} = useParams();
  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();
  const age = searchParams.get("age");
  let fromAge = "";
  let toAge = "";
  const [hasPlayedAudio, setHasPlayedAudio] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPicture, setHoverPicture] = useState<any | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  if (age) {
    [fromAge, toAge] = age.split('-');
  }
  const audioRef = useRef<HTMLAudioElement>(null);
  const {materials: {cardsList: cardsListState, oneCard: oneCardState}} = useSelector(selectStore);
  const [activePicture, setActivePicture] = useState(oneCardState?.data?.card?.pictures[0]);
  const translateY = useRef(0);
  const [cardOffset, setCardOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const touchStartY = useRef(0);

  const isLastCard = currentIndex === (cardsListState?.data?.cards?.length || 0) - 1;
  const isTablet = screens.md && !screens.xl && !screens.xxl;

const handleTouchStart = (e) => {
  if (!(screens.xs || isTablet)) return;
  touchStartY.current = e.touches[0].clientY;
  setIsAnimating(false);
};

const handleTouchMove = (e) => {
  if (!(screens.xs || isTablet)) return;
  const deltaY = e.touches[0].clientY - touchStartY.current;
  translateY.current = deltaY;
  setCardOffset(deltaY);
};

const handleTouchEnd = () => {
  if (!(screens.xs || isTablet)) return;
  const deltaY = translateY.current;

  if (deltaY < -40 && nextCard) {
    setCardOffset(-window.innerHeight);
    setIsAnimating(true);
    setTimeout(() => {
      handleSelectCard(nextCard.id);
      setCardOffset(0);
      setIsAnimating(false);
    }, 300);
  } else if (deltaY > 40 && prevCard) {
    setCardOffset(window.innerHeight); 
    setIsAnimating(true);
    setTimeout(() => {
      handleSelectCard(prevCard.id);
      setCardOffset(0);
      setIsAnimating(false);
    }, 300);
  } else {
    setCardOffset(0);
  }

  translateY.current = 0;
};


  useEffect(() => {
    dispatch(fetchCardsListAction({}));
    dispatch(fetchOneCardAction({id: currentCard}));
  }, [currentCard]);

  useEffect(() => {
    if (cardsListState?.data?.cards && currentCard) {
      const index = cardsListState?.data.cards.findIndex((card: any) => card.id === parseInt(currentCard));
      setCurrentIndex(index);
    }
  }, [cardsListState?.data?.cards, currentCard]);

  const handleSelectCard = (id: any) => {
    const path = {
      pathname: generatePath(ROUTE_PATH.CURRENT_CARD, {currentCard: id}),
    };
    embeddedNavigate(path);
  };

  const handleGoBack = () => {
    embeddedNavigate('/training-cards');
  };

  const startAudio = useCallback(() => {
  if (audioRef.current && !isMuted) {
    audioRef.current.loop = true;
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setHasPlayedAudio(true);
        })
        .catch(() => {
          setHasPlayedAudio(false);
        });
    }
  }
}, [isMuted]);

  useEffect(() => {
    startAudio();
  }, [startAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      setAudioProgress(audio.currentTime);
      setDuration(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', updateProgress);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadedmetadata', updateProgress);
    };
  }, []);

  useEffect(() => {
    const pictures = oneCardState?.data?.card?.pictures;
    if (pictures && pictures.length > 0) {
      setActivePicture(pictures[0]);
    }
  }, [oneCardState?.data?.card?.pictures]);

  useEffect(() => {
    const audio = audioRef.current;
    const pictures = oneCardState?.data?.card?.pictures;

    if (!audio || !pictures) return;

    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      const currentPic = pictures.find(
        (pic:any) => currentTime >= pic.start && currentTime < pic.end
      );
      if (currentPic && currentPic.link !== activePicture?.link) {
        setActivePicture(currentPic);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [oneCardState?.data?.card?.pictures, activePicture]);

  const getPrevNextCards = () => {
    const cards = cardsListState?.data?.cards || [];
    const prevCard = currentIndex > 0 ? cards[currentIndex - 1] : null;
    const currentCardData = cards[currentIndex];
    const nextCard = currentIndex < cards.length - 1 ? cards[currentIndex + 1] : null;

    return { prevCard, currentCardData, nextCard };
  };

  useEffect(() => {
  const handleTouch = () => {
    setIsVisible(true);
    if (!audioRef.current?.paused) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsVisible(false), 3000);
    }
  };

  document.addEventListener('touchstart', handleTouch);
  return () => {
    document.removeEventListener('touchstart', handleTouch);
  };
}, []);


 const handlePlayPause = () => {
  const audio = audioRef.current;
  if (!audio) return;

  if (audio.paused) {
    audio.play();
    setIsMuted(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(false), 3000);
  } else {
    audio.pause();
    setIsMuted(true);
    setIsVisible(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }
};

  const { prevCard, currentCardData, nextCard } = getPrevNextCards();

  const CardPreview = ({ card, opacity, onClick, position }: any) => {
    if (!card) return <Col span={screens.xs || isTablet ? 22 : 7} />;

    const isHovered = hoveredCardId === card.id;

    return (
      <Col span={screens.xs || isTablet ? 22 : 7}>
        <div
          onClick={onClick}
          onMouseEnter={() => setHoveredCardId(card.id)}
          onMouseLeave={() => setHoveredCardId(null)}
          style={{
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: isHovered ? 'scale(0.95)' : 'scale(0.9)',
            opacity: isHovered ? 1 : opacity,
          }}
        >
          {/* Эффект подсветки при наведении */}
          <div>

          <div
            style={{
              objectFit: "cover",
              position: 'absolute',
              inset: 0,
              borderRadius: 8,
              background: isHovered
                ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%)'
                : 'none',
              boxShadow: isHovered
                ? '0 8px 32px rgba(0, 0, 0, 0.12)'
                : 'none',
              transition: 'all 0.3s ease',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
                <ImageWrapper screens={screens}>
                      <Image
                        preview={false}
                        style={{
                          borderRadius: 8,
                        }}
                        src={card.preview_image}
                      />
                    </ImageWrapper>
          {/* Градиент для текста */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
              borderRadius: '0 0 8px 8px',
              pointerEvents: 'none',
            }}
          />

            <div>

              <MyText
                color="#fff"
                size={isHovered ? 22 : 20}
                weight={600}
                style={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  right: 16,
                  fontFamily: 'Nunito',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                {card.title}
              </MyText>
            </div>

          </div>
        </div>
      </Col>
    );
  };

  return (

    <>
              <Helmet>
                <title>Познавательные детские карточки</title>
                <meta name="description" content="Познавательные видео для обучения детей: речь, внимание, окружающий мир. Весёлые и полезные материалы для изучения дома и в детском саду." />
              </Helmet>


    <Row justify="center">
      <audio ref={audioRef} loop autoPlay src={oneCardState?.data?.card?.audio_link} preload="auto"/>
      <Col span={24}>
        <Row align="middle" style={{marginBottom: screens.xs || isTablet ? 10 : 26}} justify="center">
          <Col span={20}>
            <Row align="middle">

 {screens.xs ? (
              <>
                <Col span={3}>
                  <ButtonContainer onClick={handleGoBack}>
                    <LeftOutlined style={{ color: 'rgba(10, 3, 60, 0.7)' }} />
                  </ButtonContainer>
                </Col>
                <Col span={21}>
                  <MyText
                    color="rgba(19, 15, 38, 1)"
                    size={24}
                    weight={800}
                    style={{ fontFamily: 'Nunito', display: 'block', marginLeft: 12 }}
                  >
                    {oneCardState?.data?.card?.title}
                  </MyText>
                </Col>
              </>
            ) : (
              <>
                <ButtonContainer onClick={handleGoBack}>
                  <LeftOutlined style={{ color: 'rgba(10, 3, 60, 0.7)' }} />
                </ButtonContainer>
                <MyText
                  color="rgba(19, 15, 38, 1)"
                  size={32}
                  weight={800}
                  style={{ fontFamily: 'Nunito', display: 'block', marginLeft: 12 }}
                >
                  {oneCardState?.data?.card?.title}
                </MyText>
              </>
            )}
            </Row>
          </Col>
        </Row>

        <div
          style={{ position: 'relative', overflow: 'hidden' }}
           {...((screens.xs || isTablet) && {
            onTouchStart: handleTouchStart,
            onTouchMove: handleTouchMove,
            onTouchEnd: handleTouchEnd,
          })}
        >
        <Row justify="center" align="middle" style={{width: "100%", height: screens.xs ? '520px' : isTablet ? "800px" : "auto"}}>
          <Col xs={23}
               sm={23}
               md={23}
               lg={23}
               xl={20}
               xxl={20}>

            <Spin spinning={cardsListState?.loading || oneCardState?.loading} style={{width: "100%"}}>
              <Row align="middle" justify={screens.xs || isTablet ? 'center' : 'space-between'} gutter={24}>
                {/* Предыдущая карточка */}
                {!(screens.xs || isTablet) && (
                <CardPreview
                  card={prevCard}
                  opacity={0.5}
                  onClick={() => prevCard && handleSelectCard(prevCard.id)}
                  position="prev"
                />
                )} 

                {screens.xs || isTablet && prevCard && (
                <div
                  style={{
                    // position: 'absolute',
                    width: '100%',
                    transform: `translateY(${cardOffset - window.innerHeight}px)`,
                    transition: isAnimating ? 'transform 0.3s ease' : 'none',
                  }}
                >
                  <CardPreview
                                card={prevCard}
                                opacity={0.5}
                                onClick={() => prevCard && handleSelectCard(prevCard.id)}
                                position="prev"
                              />
                </div>
                )}
                {/* Текущая карточка */}
                <Col
                    span={screens.xs || isTablet ? 22 : 7}
                    style={{
                      ...((screens.xs || isTablet)
                        ? {
                            position: 'absolute',
                            width: '100%',
                            transform: `translateY(${cardOffset}px)`,
                            transition: isAnimating ? 'transform 0.3s ease' : 'none',
                            zIndex: 2,
                          }
                        : {}),
                    }}
                  >
                  <div
                    style={{
                      position: 'relative',
                      borderRadius: 8,
                      overflow: 'hidden',
                      boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {/* Контролы аудио */}
                    {/* <Row style={{position: 'absolute', top: 16, left: 16, zIndex: 3}} gutter={[12, 0]}>
                      <Col>
                        <Button
                          shape="circle"
                          icon={
                            audioRef.current?.paused || isMuted
                              ? <CaretRightFilled style={{fontSize: 20}}/>
                              : <PauseOutlined style={{fontSize: 20}}/>
                          }
                          onClick={() => {
                            const audio = audioRef.current;
                            if (audio) {
                              if (audio.paused) {
                                audio.play();
                                setIsMuted(false);
                              } else {
                                audio.pause();
                                setIsMuted(true);
                              }
                            }
                          }}
                          style={{
                            width: 48,
                            height: 48,
                            background: 'rgba(255, 255, 255, 0.95)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      </Col>
                    </Row> */}

                    {/* Индикатор картинок */}
                    {/* {oneCardState?.data?.card?.pictures?.length > 1 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          zIndex: 3,
                          background: 'rgba(0, 0, 0, 0.6)',
                          padding: '4px 12px',
                          borderRadius: 20,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {oneCardState?.data.card?.pictures.findIndex((p:any) => p.link === activePicture?.link) + 1} / {oneCardState?.data.card?.pictures.length}
                      </div>
                    )} */}

                    <ImageWrapper screens={screens}>
                      {/* <div
        style={{
          position: 'relative',
          paddingTop: 30,
          paddingBottom: 30,
        }}
      > */}
                      {/* {activePicture?.link && (
  <>
    <img
      src={activePicture.link}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 35,
        width: '100%',
        objectFit: 'cover',
        objectPosition: 'top',
        filter: 'blur(5px) brightness(0.6)',
        zIndex: 1,
        pointerEvents: 'none',
      }}
      alt=""
    />
    <img
      src={activePicture.link}
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 35,
        width: '100%',
        objectFit: 'cover',
        objectPosition: 'bottom',
        filter: 'blur(5px) brightness(0.6)',
        zIndex: 1,
        pointerEvents: 'none',
      }}
      alt=""
    />
  </>
)} */}
                      <div
                        style={{
                          position: 'relative',
                          zIndex: 2,
                        }}
                      >

                      <Row
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 3,
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                      }}
                      gutter={[12, 0]}
                    >
                      <Col>
                        <Button
                          shape="circle"
                          icon={
                            audioRef.current?.paused || isMuted
                              ? <CaretRightFilled style={{ fontSize: 20 }} />
                              : <PauseOutlined style={{ fontSize: 20 }} />
                          }
                          onClick={handlePlayPause}
                          style={{
                            width: 48,
                            height: 48,
                            background: 'rgba(255, 255, 255, 0.95)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      </Col>
                    </Row>
                      <StyledImage
                        preview={false}
                        src={activePicture?.link}
                        isPlaying={!audioRef.current?.paused}
                      />
                        </div>
                      {/* </div>   */}
                    </ImageWrapper>
                    {/* Градиент сверху */}
                    {/* <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '10%',
                        background: 'linear-gradient(rgba(0, 0, 0, 0.35), transparent), radial-gradient(at center top, rgba(0, 0, 0, 0) 100%, rgba(0, 0, 0, 0.6) 20%)',
                        backgroundBlendMode: 'multiply',
                        pointerEvents: 'none',
                        zIndex: 3,
                      }}
                    /> */}
                    {/* Градиент снизу */}
                    {/* <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '20%',
                      background: `
                        linear-gradient(to top, rgba(0, 0, 0, 0.35), transparent),
                        radial-gradient(at center bottom, rgba(0, 0, 0, 0) 100%, rgba(0, 0, 0, 0.6) 40%)
                      `,
                      backgroundBlendMode: 'multiply',
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  /> */}

                    <MyText
                      color="rgb(255, 255, 255)"
                      size={screens.xs ? 18 : isTablet ? 26 : 32}
                      weight={700}
                      style={{
                        fontFamily: 'Nunito',
                        position: 'absolute',
                        bottom: 60,
                        left: screens.xs || isTablet ? 18 : 24,
                        right: 24,
                        zIndex: 3,
                        textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                      }}
                    >
                      {oneCardState?.data?.card?.title}
                    </MyText>

                    {/* Прогресс бар */}
                    <div
                    style={{
                      position: 'absolute',
                      width: '100%',
                      zIndex: 3,
                      bottom: '20px'
                    }}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percent = x / rect.width;
                      const time = percent * duration;
                      setHoverTime(time);
                      setHoverX(x);

                      const pictures = oneCardState?.data?.card?.pictures;
                      if (pictures) {
                        const currentPic = pictures.find(
                          (pic: any) => time >= pic.start && time < pic.end
                        );
                        setHoverPicture(currentPic || null);
                      }
                    }}
                    onMouseLeave={() => {
                      setHoverTime(null);
                      setHoverPicture(null);
                      setHoverX(null);
                    }}
                  >
                  {isHovered && (
                  <>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 40,
                        left: hoverX ?? 0,
                        transform: 'translateX(-50%)',
                        zIndex: 5,
                        pointerEvents: 'none',
                        transition: 'all 0.1s ease',
                        display: hoverPicture ? 'block' : 'none',
                      }}
                    >
                      <div
                        style={{
                          background: 'rgba(0, 0, 0, 0.75)',
                          color: '#fff',
                          padding: 6,
                          borderRadius: 6,
                          fontSize: 12,
                          textAlign: 'center',
                          maxWidth: 160,
                        }}
                      >
                        <div>
                          {hoverTime !== null &&
                            `${Math.floor(hoverTime / 60)}:${Math.floor(hoverTime % 60)
                              .toString()
                              .padStart(2, '0')}`}
                        </div>
                        {hoverPicture?.link && (
                          <img
                            src={hoverPicture.link}
                            alt=""
                            style={{
                              marginTop: 4,
                              width: '100%',
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <div
                    style={{
                      background: 'transparent',
                      padding: '0 12px',
                    }}
                  >
                    <Slider
                      min={0}
                      max={duration}
                      step={0.1}
                      value={audioProgress}
                      tooltip={{
                        formatter: (value: any) => {
                          const minutes = Math.floor(value / 60);
                          const seconds = Math.floor(value % 60);
                          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        },
                      }}
                      onChange={(value) => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = value;
                        }
                      }}
                      styles={{
                        rail: {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          height: 6,
                        },
                        track: {
                          backgroundColor: '#1890ff',
                          height: 6,
                        },
                        handle: {
                          opacity: isHovered ? 1 : 0,
                          width: 14,
                          height: 14,
                        },
                      }}
                    />
                  </div>
                  </>
  )}
                  </div>
                  </div>
                </Col>

                {screens.xs || isTablet && nextCard && (
                  <div
                    style={{
                      position: 'absolute',
                      width: '100%',
                      transform: `translateY(${cardOffset + window.innerHeight}px)`,
                      transition: isAnimating ? 'transform 0.3s ease' : 'none',
                    }}
                  >
                    <CardPreview
                                  card={nextCard}
                                  opacity={0.5}
                                  onClick={() => nextCard && handleSelectCard(nextCard.id)}
                                  position="next"
                                />
                  </div>
                )}

                {screens.xs || isTablet && isLastCard && (
                  <FloatButton
                    onClick={() => {
                      setCardOffset(-window.innerHeight);
                      setIsAnimating(true);
                      setTimeout(() => {
                        handleSelectCard(cardsListState.data.cards[0]?.id);
                        setCardOffset(0);
                        setIsAnimating(false);
                      }, 300);
                    }}
                    icon={<ArrowUpOutlined />}
                    style={{
                      position: 'fixed',
                      bottom: 80,
                      right: 16,
                      zIndex: 1000,
                    }}
                    type="primary"
                  />
                )}

                {!(screens.xs || isTablet) && (
                <CardPreview
                  card={nextCard}
                  opacity={0.5}
                  onClick={() => nextCard && handleSelectCard(nextCard.id)}
                  position="next"
                />
                )}
              </Row>
            </Spin>
          </Col>

        </Row>
        </div>
      </Col>
    </Row>
    </>
  );
};